import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Roles } from '@app/core/guard/Roles';
import { PartnerUserResponse } from '@interfaces/partners/responses/partner-user.response';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { RouterLink } from '@angular/router';
import { ModalPartnerUserComponent } from './components/modal-partner-user/modal-partner-user.component';

/**
 * Comercial › Usuários: a equipe do próprio parceiro, administrada pelo **master** dele
 * (17/09/2026).
 *
 * Existe porque dar um usuário novo a um parceiro era tarefa da Legatus, e quem sabe quem
 * entrou e quem saiu da equipe é o parceiro. O caso que doía não era o cadastro que demora —
 * era o **desligamento**: enquanto ninguém da Legatus atendia, um ex-funcionário do parceiro
 * continuava entrando e enxergando os deals e as empresas dele.
 *
 * A tela só existe para quem tem `COMMERCIAL_MANAGE_USERS`, que vive no grupo
 * `Parceiro Comercial - Master`. Usuário comum do parceiro não vê o item no menu e a rota
 * responde negada.
 *
 * ⚠️ **As ações não alcançam usuário master** — nem o próprio logado. Eles aparecem na lista
 * com o perfil marcado, só sem menu de ação. São duas razões, e a segunda decide:
 * (a) inativar a si mesmo trancaria o master fora desta tela, e só a Legatus destravaria;
 * (b) um parceiro com dois masters, porque a Legatus promoveu alguém, teria um podendo
 * inativar o outro — o que é, na prática, tomar o parceiro. O backend recusa de todo modo;
 * omitir as ações aqui evita o clique que vira erro.
 */
@Component({
  selector: 'app-partner-users',
  standalone: true,
  // `RouterLink` explícito: o `SharedModule` não reexporta o `RouterModule`, e sem ele o
  // `routerLink` do aviso compilaria, nasceria sem `href` e não navegaria — sem erro nenhum
  // no console. Pegadinha já documentada no README do módulo Comercial.
  imports: [CommonModule, RouterLink, SharedModule, ModalPartnerUserComponent],
  templateUrl: './partner-users.component.html',
  styleUrls: ['./partner-users.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerUsersComponent extends VaultBaseComponent implements OnInit {
  @ViewChild('modalUsuario') modalUsuario!: ModalPartnerUserComponent;

  breadcrumbs: MenuItem[] = [];
  home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };

  readonly usuarios = signal<PartnerUserResponse[]>([]);
  readonly loading = signal(false);

  /**
   * Se quem abriu a tela é mesmo um usuário de parceiro.
   *
   * Existe porque a **rota é alcançável por URL direta** para um usuário interno: o
   * `PermissionGuard` só olha a permissão, e o grupo FULL do TENANT recolhe
   * `COMMERCIAL_MANAGE_USERS` sozinho no startup (o enum a declara para PARTNER **e**
   * TENANT). Sem esta checagem a tela abria para a Legatus mostrando "0 usuários na sua
   * equipe" e um botão Cadastrar que sempre falharia com "não foi possível identificar o
   * parceiro do usuário logado" — o backend recusa certo, mas a tela prometia o que não
   * podia cumprir. Medido no navegador em 18/09/2026.
   *
   * O sinal é o **papel**, o mesmo que o menu usa para decidir em qual entrada o item
   * aparece — não um segundo critério para a mesma pergunta. E o remédio é o mesmo do
   * `user-access-panel` em `partnerMode`: em vez de UI que não funciona, um aviso
   * apontando para a tela certa.
   */
  readonly ehParceiro = signal(false);

  readonly columnDefinitions: ColumnDefinition[] = [
    new ColumnDefinition({ field: 'nome', header: 'Nome' }),
    new ColumnDefinition({ field: 'email', header: 'E-mail' }),
    new ColumnDefinition({ field: 'phoneNumber', header: 'Telefone' }),
    new ColumnDefinition({ field: 'perfil', header: 'Perfil', template: 'perfil' }),
    new ColumnDefinition({ field: 'acesso', header: 'Acesso', template: 'acesso' }),
    new ColumnDefinition({ field: 'situacao', header: 'Situação', template: 'situacao' }),
  ];

  constructor(
    translateService: TranslateService,
    private readonly partnersService: PartnersHttpService,
    private readonly toast: ToastHelperService,
    private readonly confirmationService: ConfirmationService,
    private readonly permissionsService: PermissionsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef
  ) {
    super(translateService);
  }

  ngOnInit(): void {
    this.breadcrumbs = [
      { label: 'Comercial' },
      { label: 'Usuários', routerLink: '/dashboard/commercial/users' },
    ];

    this.permissionsService
      .hasRole(Roles.PARTNER)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(ehParceiro => {
        this.ehParceiro.set(ehParceiro);
        this.cdr.markForCheck();

        // Só busca quando faz sentido: para usuário interno a resposta é sempre lista
        // vazia (o backend resolve o parceiro pelo token), e a requisição seria gasto puro.
        if (ehParceiro) this.carregar();
      });
  }

  /**
   * Carrega a equipe.
   *
   * O endpoint não recebe id de parceiro: ele resolve pelo usuário logado. Não há nada para
   * a tela passar, e é justamente isso que impede a tela de pedir a equipe de outro parceiro.
   */
  carregar(): void {
    this.loading.set(true);

    this.partnersService.getMyPartnerUsers().subscribe({
      next: response => {
        this.usuarios.set(this.comCamposDeExibicao(response || []));
        this.loading.set(false);
        this.cdr.markForCheck();
      },
      error: error => {
        this.loading.set(false);
        this.cdr.markForCheck();
        this.toast.showToastError(
          'Erro ao carregar os usuários',
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  /**
   * Ações da linha.
   *
   * **Omitidas** na linha de master, e não desabilitadas: menu com três itens cinzas é pior
   * que menu ausente — é a mesma lição do checkbox da aba Empresas do parceiro, que o Breno
   * pediu para esconder em vez de desabilitar.
   *
   * "Reenviar convite" também sai quando a pessoa já criou a senha: não há convite pendente.
   *
   * "Promover para Master" aparece em toda linha comum — inclusive na de quem ainda não criou
   * a senha, porque o perfil não depende de ele já ter entrado. Não existe o inverso aqui:
   * ver {@link promoverParaMaster}.
   */
  acoesDaLinha(usuario: PartnerUserResponse): MenuItem[] {
    if (usuario.isMaster) return [];

    const acoes: MenuItem[] = [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        command: () => this.modalUsuario.abrirParaEditar(usuario),
      },
    ];

    if (!usuario.hasPassword) {
      acoes.push({
        label: 'Reenviar convite',
        icon: 'pi pi-envelope',
        command: () => this.reenviarConvite(usuario),
      });
    }

    acoes.push({
      label: 'Promover para Master',
      icon: 'pi pi-star',
      command: () => this.promoverParaMaster(usuario),
    });

    acoes.push(
      usuario.active
        ? {
            label: 'Inativar',
            icon: 'pi pi-ban',
            styleClass: 'delete-btn',
            command: () => this.alternarAcesso(usuario, false),
          }
        : {
            label: 'Reativar',
            icon: 'pi pi-check-circle',
            command: () => this.alternarAcesso(usuario, true),
          }
    );

    return acoes;
  }

  /**
   * Promove um usuário comum a master, com confirmação.
   *
   * ⚠️ **Não existe o inverso nesta tela**, e a confirmação diz isso com todas as letras:
   * assim que ele vira master, `acoesDaLinha` devolve `[]` para a linha dele e o menu inteiro
   * some — inclusive editar e inativar. Desfazer é da Legatus.
   *
   * Por isso o texto não é "tem certeza?": ele enumera o que o master ganha e o que o
   * promotor perde. Confirmação que só pergunta "tem certeza" treina a pessoa a clicar em
   * Sim sem ler, e esta é justamente a ação em que ler importa.
   */
  private promoverParaMaster(usuario: PartnerUserResponse): void {
    const nome = this.nomeCompleto(usuario);

    this.confirmationService
      .confirm(
        `Promover ${nome} para Master?`,
        'Ele passa a ver os deals de toda a equipe e a administrar esta tela, podendo cadastrar e inativar outros usuários. Atenção: esta ação não tem volta por aqui — depois de promovido, você não conseguirá editar, inativar nem rebaixar este usuário. Só a Legatus desfaz.'
      )
      .subscribe(confirmado => {
        if (!confirmado) return;

        this.partnersService.promoteMyPartnerUserToMaster(usuario.id).subscribe({
          next: () => {
            this.toast.showToastSuccess('Usuário promovido', `${nome} agora é Master do parceiro.`);
            this.carregar();
          },
          error: error => {
            this.toast.showToastError(
              'Erro ao promover o usuário',
              error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
            );
          },
        });
      });
  }

  /** Abre a modal em modo cadastro. */
  abrirCadastro(): void {
    this.modalUsuario.abrirParaCriar();
  }

  /**
   * Trava ou destrava o login, com confirmação.
   *
   * ⚠️ O texto do inativar avisa que **a sessão aberta continua valendo por até uma hora**.
   * A checagem de acesso travado está no login, e um token já emitido não sabe que a pessoa
   * foi inativada. Sem esse aviso, o master inativa alguém, vê o nome dele num deal minutos
   * depois e conclui que não funcionou.
   */
  private alternarAcesso(usuario: PartnerUserResponse, ativar: boolean): void {
    const nome = this.nomeCompleto(usuario);

    const titulo = ativar ? `Reativar ${nome}?` : `Inativar ${nome}?`;
    const descricao = ativar
      ? 'Ele volta a conseguir entrar na plataforma com o mesmo e-mail e a mesma senha.'
      : 'Ele deixa de conseguir entrar na plataforma. Os deals que ele registrou continuam valendo, e você pode reativá-lo depois. Atenção: se ele estiver com a plataforma aberta agora, a sessão dele pode continuar valendo por até uma hora.';

    this.confirmationService.confirm(titulo, descricao).subscribe(confirmado => {
      if (!confirmado) return;

      this.partnersService.setMyPartnerUserActive(usuario.id, ativar).subscribe({
        next: () => {
          this.toast.showToastSuccess(ativar ? 'Usuário reativado' : 'Usuário inativado', '');
          this.carregar();
        },
        error: error => {
          this.toast.showToastError(
            ativar ? 'Erro ao reativar o usuário' : 'Erro ao inativar o usuário',
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
    });
  }

  /** Reenvia o link de criar senha, com confirmação — a ação manda e-mail. */
  private reenviarConvite(usuario: PartnerUserResponse): void {
    this.confirmationService
      .confirm(
        `Reenviar o convite para ${this.nomeCompleto(usuario)}?`,
        `Vamos mandar de novo, para ${usuario.email}, o link de criar a senha de acesso.`
      )
      .subscribe(confirmado => {
        if (!confirmado) return;

        this.partnersService.resendMyPartnerUserInvite(usuario.id).subscribe({
          next: () => this.toast.showToastSuccess('Convite reenviado', ''),
          error: error => {
            this.toast.showToastError(
              'Erro ao reenviar o convite',
              error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
            );
          },
        });
      });
  }

  /**
   * Acrescenta os campos que a tabela exibe e que não vêm do backend.
   *
   * Formatação de exibição fica no frontend de propósito (a régua do projeto manda cálculo
   * para o backend, não concatenação de nome). `nome` existe porque a `ColumnDefinition`
   * aponta para um campo, não para uma expressão.
   */
  private comCamposDeExibicao(usuarios: PartnerUserResponse[]): PartnerUserResponse[] {
    return usuarios.map(usuario => ({
      ...usuario,
      nome: this.nomeCompleto(usuario),
      perfil: usuario.isMaster ? 'Master' : 'Comum',
      acesso: usuario.hasPassword ? 'Senha criada' : 'Convite pendente',
      situacao: usuario.active ? 'Ativo' : 'Inativo',
    })) as PartnerUserResponse[];
  }

  private nomeCompleto(usuario: PartnerUserResponse): string {
    return [usuario.firstName, usuario.surName].filter(Boolean).join(' ').trim();
  }
}
